import {
  GOOD_SCORE,
  GOOD_WINDOW_MS,
  MISS_SCORE,
  OK_SCORE,
  PERFECT_SCORE,
  PERFECT_WINDOW_MS,
  type JudgeVerdict,
} from './gameTypes.js';

export interface JudgeResult {
  verdict: JudgeVerdict;
  diffMs: number;
  baseScore: number;
}

/**
 * Per-call override for the judgement windows. Circles uses this to
 * tighten the timing tolerance on Hard / Extreme variants — players
 * said the "halfway through the approach is still good" feel ought
 * to scale with difficulty, so each variant ships its own ms windows
 * rather than the global Pulse constants.
 *
 * `okWindowMs` controls the third "mercy" tier (osu!'s 50). It is
 * **opt-in**: when omitted, judge() returns only perfect / good / miss
 * (matching the pre-OK behaviour). Modes that want the mercy band
 * (Circles) pass an explicit value; modes that don't (Pulse) leave
 * it undefined and keep their three-tier behaviour unchanged.
 */
export interface JudgeOptions {
  perfectWindowMs?: number;
  goodWindowMs?: number;
  okWindowMs?: number;
}

export function judge(
  actualMs: number,
  targetMs: number,
  opts: JudgeOptions = {},
): JudgeResult {
  const perfectMs = opts.perfectWindowMs ?? PERFECT_WINDOW_MS;
  const goodMs = opts.goodWindowMs ?? GOOD_WINDOW_MS;
  const diffMs = actualMs - targetMs;
  const absDiff = Math.abs(diffMs);
  if (absDiff <= perfectMs) {
    return { verdict: 'perfect', diffMs, baseScore: PERFECT_SCORE };
  }
  if (absDiff <= goodMs) {
    return { verdict: 'good', diffMs, baseScore: GOOD_SCORE };
  }
  if (opts.okWindowMs !== undefined && absDiff <= opts.okWindowMs) {
    return { verdict: 'ok', diffMs, baseScore: OK_SCORE };
  }
  return { verdict: 'miss', diffMs, baseScore: MISS_SCORE };
}

export function comboMultiplier(combo: number): number {
  if (combo < 8) return 1;
  if (combo < 24) return 1.25;
  if (combo < 64) return 1.5;
  return 2;
}

/**
 * Accuracy weights mirror osu!'s ScoreV1 distribution scaled by the
 * highest base value: perfect (300) → 1.0, good (100) → 0.333,
 * ok (50) → 0.167, miss (0) → 0. Matching osu! makes accuracy
 * comparable across the games — a 92% Circles run reads about the
 * same as a 92% osu! run.
 */
const ACCURACY_WEIGHT_GOOD = 1 / 3;
const ACCURACY_WEIGHT_OK = 1 / 6;

export function computeAccuracy(
  perfects: number,
  goods: number,
  oks: number,
  misses: number,
): number {
  const total = perfects + goods + oks + misses;
  if (total === 0) return 0;
  return (
    (perfects + goods * ACCURACY_WEIGHT_GOOD + oks * ACCURACY_WEIGHT_OK) / total
  );
}
